/**
 * iter175 — Email Verification Banner.
 *
 * Persistent amber banner rendered at the top of the DashboardLayout when the
 * signed-in user has `email_verified === false`. Exposes a "Resend
 * verification email" button (throttled server-side to 60s) so users can fix
 * a typoed email without support.
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const EmailVerificationBanner = ({ user }) => {
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);

  if (!user || user.email_verified !== false) return null;

  const handleResend = async () => {
    try {
      setSending(true);
      await api.post('/auth/resend-verification');
      setJustSent(true);
      toast.success('Verification email sent. Check your inbox.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not resend verification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-amber-500/10 border-b border-amber-500/40 px-4 py-2.5 flex items-center justify-between gap-3"
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-2 text-sm text-amber-100">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-300" />
        <span>
          Please verify your email <strong>{user.email}</strong> to unlock Signals and AI features.
        </span>
      </div>
      <button
        type="button"
        onClick={handleResend}
        disabled={sending || justSent}
        className="text-xs font-semibold text-amber-900 bg-amber-300 hover:bg-amber-200 px-3 py-1 rounded-md flex-shrink-0 transition-colors disabled:opacity-60 inline-flex items-center gap-1.5"
        data-testid="resend-verification-btn"
      >
        {sending ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</>
        ) : justSent ? (
          <><MailCheck className="h-3.5 w-3.5" /> Sent — check your inbox</>
        ) : (
          <>Resend verification email</>
        )}
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
